import { configureStore } from '@reduxjs/toolkit';
import type { CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import {
    FLUSH,
    PAUSE,
    PERSIST,
    createMigrate,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE
} from 'redux-persist';
import { createStorage, isReduxDevToolsEnabled } from '~/store/utils';
import { version, migrations } from './migrations';
import { reducers } from './reducers';
import { rootPersistConfig } from './persistConfig';
import {
    resetStateOnSelectedDateChangeMiddleware,
    requestUnassignedTasksForDateRangeMiddleware,
    requestAllTasksOnSelectionChange
} from '~/store/middleware';
import { getReduxDebugMigration } from '~/env';

// enable devTools only for `local`, `stage`, and `sandbox` environments
const isDevToolsEnabled = isReduxDevToolsEnabled();

// enable migration debugging messages for the browser only when dev tools are enabled
const isMigrateDebugEnabled = isDevToolsEnabled && getReduxDebugMigration();

export type ReduxState = ReturnType<typeof reducers>;

export const storage = createStorage();

const persistConfig = {
    ...rootPersistConfig,
    storage,
    version,
    migrate: createMigrate(migrations, {
        debug: isMigrateDebugEnabled
    })
};

const persistedReducer = persistReducer(persistConfig, reducers);

// updated middleware callback per [Redux Toolkit getDefaultMiddleware](https://redux-toolkit.js.org/api/getDefaultMiddleware)
/** @TODO Danny: Figure out how to type this correctly */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const defaultMiddleware: any = (
    getDefaultMiddleware: CurriedGetDefaultMiddleware<ReduxState>
) => {
    return [
        ...getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER
                ]
            }
        }),
        resetStateOnSelectedDateChangeMiddleware,
        requestAllTasksOnSelectionChange,
        requestUnassignedTasksForDateRangeMiddleware
    ];
};

export const store = configureStore({
    devTools: isDevToolsEnabled,
    reducer: persistedReducer,
    middleware: defaultMiddleware
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;
